import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeDailyReportProperties,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import { useLocation } from "react-router-dom";
import MultiSelect, { Option } from "../../components/Forms/MultiSelect.tsx";
import DailyReportDataGrid from "../../components/DataTables/DailyReportDataGrid.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";

interface DailyReportResult {
  PremiumSum?: string;
  GFSum?: string;
  StickerSum?: string;
  "2PercSum"?: string;
  CommissionSum?: string;
  Contracts?: string;
  AllTrans?: string;
  Total_Sum?: string;
  [key: string]: any;
}

const labelClass =
  "mb-1 ml-1 block text-xs font-medium text-black dark:text-white";

const DailyReport: React.FC = () => {
  const [formState, setFormState] = useState({
    fromDate: "",
    toDate: "",
    company: "",
    policyType: "",
    agent: "",
    paymentPlaces: [],
    selectedOffices: [],
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [offices, setOffices] = useState<{ [key: string]: string }>({});
  const [officesOptions, setOfficesOptions] = useState<Option[]>([]);
  const [selectedOfficeValues, setSelectedOfficeValues] = useState<string[]>(
    []
  );
  const [paymentPlaces, setPaymentPlaces] = useState<{ [key: string]: string }>(
    {}
  );
  const [paymentPlacesOptions, setPaymentPlacesOptions] = useState<Option[]>(
    []
  );
  const [selectedPaymentPlacesValues, setSelectedPaymentPlacesValues] =
    useState<string[]>([]);
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState<any | null>(null);
  const [showSummary, setShowSummary] = useState(false);

  const location = useLocation();

  useEffect(() => {
    setFormState({
      fromDate: "",
      toDate: "",
      company: "",
      policyType: "",
      agent: "",
      paymentPlaces: [],
      selectedOffices: [],
    });
    setPolicies([]);
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [
          agents,
          companies,
          officesRes,
          policyTypesRes,
          paymentPlacesRes,
        ] = await Promise.all([
          InsuranceService.getAllAgents(jwt),
          InsuranceService.getAllInsuranceCompanies(jwt),
          InsuranceService.getAllOffices(jwt),
          InsuranceService.getAllPolicyTypes(jwt),
          PolicyService.fetchPayPlaces(jwt),
        ]);
        setAgentOptions({
          0: "Всички",
          ...(agents as { [key: string]: string }),
        });

        setInsuranceCompanies({
          0: "Всички",
          ...(companies as { [key: string]: string }),
        });

        setPolicyTypes({
          0: "Всички",
          ...(policyTypesRes as { [key: string]: string }),
        });
        setOffices(officesRes as { [key: string]: string });
        setPaymentPlaces(paymentPlacesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  useEffect(() => {
    setOfficesOptions(
      Object.entries(offices).map(([k, v]) => ({
        text: v,
        value: k,
        id: k,
        selected: selectedOfficeValues.includes(k),
      }))
    );
  }, [offices, selectedOfficeValues]);

  useEffect(() => {
    setPaymentPlacesOptions(
      Object.entries(paymentPlaces).map(([k, v]) => ({
        text: v,
        value: k,
        id: k,
        selected: selectedPaymentPlacesValues.includes(k),
      }))
    );
  }, [paymentPlaces, selectedPaymentPlacesValues]);

  const handleOfficeSelectionChange = (selectedValues: string[]) => {
    setSelectedOfficeValues(selectedValues);
    setFormState((prev) => ({ ...prev, selectedOffices: selectedValues }));
    setOfficesOptions(
      selectedValues.map((value) => ({
        text: offices[value],
        value,
        id: value,
        selected: true,
      }))
    );
  };

  const handlePaymentPlaceSelectionChange = (selectedValues: string[]) => {
    setSelectedPaymentPlacesValues(selectedValues);
    setFormState((prev) => ({ ...prev, paymentPlaces: selectedValues }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.fromDate) {
      setAlert("Моля, въведете стартова дата");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      setSummary(null);
      const result: DailyReportResult = await PolicyService.fetchDailyReport(
        {
          ...formState,
          fromDate: revertNormalizeDate(formState.fromDate),
          selectedOffices: formState.selectedOffices,
          paymentPlaces: formState.paymentPlaces,
        },
        jwt
      );
      const summaryDetails = Object.values(result)
        .filter((el) => typeof el !== "object")
        .join("\n");
      setSummary(summaryDetails);
      const normalizedPolicies = Object.values(result)
        .filter((policy) => typeof policy === "object")
        .map((policy) => normalizeDailyReportProperties(policy));
      setPolicies(normalizedPolicies);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  return (
    <>
      <Breadcrumb pageName="Дневно приключване" />

      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <FadeAlert
          alert={alert}
          type={alertType}
          onClose={() => setAlert(null)}
        />

        <SectionHeader title="Дневно приключване" />

        <form onSubmit={handleSearch} className="p-3">
          {/* Row 1 — 4 cols */}
          <div className="mb-2 mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
              />
            </div>
            <div>
              <DatePicker
                label="До дата"
                name="toDate"
                onChange={handleInputChange}
                value={formState.toDate}
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Изберете агент"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
          </div>

          <div className="mb-2 grid grid-cols-2 items-end gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>

            <div>
              <label className={labelClass}>Офис</label>
              <MultiSelect
                options={officesOptions}
                setOptions={setOfficesOptions}
                placeholder="Изберете офис"
                onSelectionChange={handleOfficeSelectionChange}
                id="multi-select-office"
              />
            </div>

            <div>
              <label className={labelClass}>Място на плащане</label>
              <MultiSelect
                options={paymentPlacesOptions}
                setOptions={setPaymentPlacesOptions}
                placeholder="Изберете място"
                onSelectionChange={handlePaymentPlaceSelectionChange}
                id="multi-select-payment-place"
              />
            </div>

            <div className="flex h-full items-end">
              <button
                type="submit"
                className="bg-primary w-full justify-center rounded px-3 py-2 text-xs font-medium text-white hover:bg-opacity-90"
              >
                Покажи
              </button>
            </div>
          </div>
        </form>

        <DailyReportDataGrid
          policies={policies}
          isLoading={isLoading}
          formState={formState}
          summary={summary}
          labelMaps={{
            agents: agentOptions,
            companies: insuranceCompanies,
            offices: offices,
            policyTypes: policyTypes,
            paymentPlaces: paymentPlaces,
          }}
        />

        {summary && (
          <div className="border-stroke dark:border-strokedark dark:bg-boxdark m-4 border bg-white">
            <div
              onClick={() => setShowSummary((prev) => !prev)}
              className="border-stroke dark:border-strokedark flex cursor-pointer items-center justify-between border-b px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200"
            >
              <span>📊 Обобщение</span>

              <span className="text-[10px] text-slate-500 hover:opacity-80">
                {showSummary ? "Скрий" : "Покажи"}
              </span>
            </div>

            {showSummary && (
              <pre className="px-3 py-3 text-xs text-slate-700 dark:text-slate-300">
                {summary}
              </pre>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DailyReport;
